<template>
  <div class="riding-issues">
    <div class="issues-overview">
      <div 
        v-for="(issue, index) in issues" 
        :key="index"
        class="issue-card"
        :class="{ 'active': selectedIssue === index }"
        @click="selectIssue(index)"
      >
        <div class="issue-icon" :style="{ color: issue.color }">
          <i :class="issue.icon"></i>
        </div>
        <div class="issue-info">
          <div class="issue-title">{{ issue.title }}</div>
          <div class="issue-severity" :style="{ color: issue.color }">
            {{ issue.severity }}
          </div>
        </div>
      </div>
    </div>

    <div class="issue-details" v-if="selectedIssue !== -1">
      <div class="detail-header">
        <h4>{{ issues[selectedIssue].title }}详情</h4>
        <div class="severity-badge" :style="{ backgroundColor: issues[selectedIssue].color }">
          {{ issues[selectedIssue].severity }}
        </div>
      </div>

      <div class="problem-description">
        <h5>问题描述</h5>
        <p>{{ issues[selectedIssue].description }}</p>
      </div>

      <div class="data-support">
        <h5>数据支持</h5>
        <div class="data-grid">
          <div 
            v-for="(data, index) in issues[selectedIssue].data" 
            :key="index"
            class="data-item"
          >
            <div class="data-label">{{ data.label }}</div>
            <div class="data-value" :style="{ color: issues[selectedIssue].color }">
              {{ data.value }}
            </div>
            <div class="data-context">{{ data.context }}</div>
          </div>
        </div>
      </div>

      <div class="solutions">
        <h5>改进措施</h5>
        <div class="solution-list">
          <div 
            v-for="(solution, index) in issues[selectedIssue].solutions" 
            :key="index"
            class="solution-item"
          >
            <div class="solution-type">{{ solution.type }}</div>
            <div class="solution-desc">{{ solution.description }}</div>
            <div class="solution-source">{{ solution.source }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

interface DataPoint {
  label: string
  value: string
  context: string
}

interface Solution {
  type: string
  description: string
  source: string
}

interface RidingIssue {
  title: string
  icon: string
  color: string
  severity: string
  description: string
  data: DataPoint[]
  solutions: Solution[]
}

const selectedIssue = ref(0)

const issues: RidingIssue[] = [
  {
    title: '车厢噪声过大',
    icon: 'fas fa-volume-up',
    color: '#ef4444',
    severity: '严重问题',
    description: '运行噪声导致乘客不适，甚至存在听力风险，特别是在高速运行和曲线段',
    data: [
      {
        label: '11号线峰值噪音',
        value: '107分贝',
        context: '触发手机健康警报'
      },
      {
        label: '12号线高噪音',
        value: '近90分贝',
        context: '摩擦声严重'
      },
      {
        label: '平均噪音范围',
        value: '70-90分贝',
        context: '超过舒适标准'
      },
      {
        label: '连接处摩擦',
        value: '尖锐异响',
        context: '11号线问题突出'
      }
    ],
    solutions: [
      {
        type: '轨道维护',
        description: '定期打磨钢轨表面，改善平顺性',
        source: '专家毛东兴建议'
      },
      {
        type: '减震措施',
        description: '采用阻尼钢轨，加装吸声装置',
        source: '专家毛东兴建议'
      },
      {
        type: '整改计划',
        description: '延长降噪整改周期，系统性解决',
        source: '申通回应'
      }
    ]
  },
  {
    title: '报站语音不清晰',
    icon: 'fas fa-microphone-slash',
    color: '#f59e0b',
    severity: '影响出行',
    description: '报站声被车厢噪声掩盖，乘客听不清到站信息，影响正常出行',
    data: [
      {
        label: '12号线85分贝时',
        value: '报站声模糊',
        context: '语音被掩盖'
      },
      {
        label: '12号线90分贝时',
        value: '完全听不清',
        context: '摩擦声掩盖'
      },
      {
        label: '低频噪声影响',
        value: '掩盖高频语音',
        context: '物理原理限制'
      },
      {
        label: '旧车型问题',
        value: '设备故障率高',
        context: '报站设备老化'
      }
    ],
    solutions: [
      {
        type: '内容优化',
        description: '删减过长提示，突出到站信息',
        source: '申通回应'
      },
      {
        type: '播报改进',
        description: '提前播报时间，增加重复频次',
        source: '专家滕靖建议'
      },
      {
        type: '线路排查',
        description: '排摸高噪声线路，重点治理',
        source: '系统性改进'
      }
    ]
  },
  {
    title: '电子显示屏故障',
    icon: 'fas fa-tv',
    color: '#8b5cf6',
    severity: '设备问题',
    description: '显示屏故障、信息被遮挡或显示时间过短，影响乘客获取到站信息',
    data: [
      {
        label: '2号线问题',
        value: '蓝屏死机',
        context: '无任何报站信息'
      },
      {
        label: '4号线问题',
        value: '被扶手遮挡',
        context: '乘客需蹲下查看'
      },
      {
        label: '9号线问题',
        value: '电子屏未启用',
        context: '设备闲置'
      },
      {
        label: '广告占用',
        value: '优先级错误',
        context: '影响关键信息'
      }
    ],
    solutions: [
      {
        type: '显示优化',
        description: '固定显示到站信息，取消滚动模式',
        source: '申通回应'
      },
      {
        type: '设备升级',
        description: '增设/维护电子屏，老车型优先',
        source: '专家滕靖建议'
      },
      {
        type: '信息管理',
        description: '优化信息优先级，到站前仅显示关键信息',
        source: '专家滕靖建议'
      }
    ]
  }
]

const selectIssue = (index: number) => {
  selectedIssue.value = index
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600;700&display=swap');

.riding-issues {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
  overflow: hidden;
  font-family: 'Noto Serif SC', serif;
}

.issues-overview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px;
  background: white;
  border-bottom: 2px solid #e2e8f0;
}

.issue-card {
  padding: 16px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
  text-align: center;
}

.issue-card:hover {
  border-color: #0085C3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.issue-card.active {
  border-color: #0085C3;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.issue-icon {
  font-size: 28px;
  margin-bottom: 12px;
}

.issue-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.issue-severity {
  font-size: 12px;
  font-weight: 500;
}

.issue-details {
  padding: 16px 20px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #0085C3;
}

.detail-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  font-family: 'Noto Serif SC', serif;
}

.severity-badge {
  padding: 6px 12px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.problem-description,
.data-support,
.solutions {
  margin-bottom: 20px;
}

.problem-description h5,
.data-support h5,
.solutions h5 {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  font-family: 'Noto Serif SC', serif;
}

.problem-description h5::before,
.data-support h5::before,
.solutions h5::before {
  content: '';
  width: 4px;
  height: 16px;
  background: #0085C3;
  margin-right: 8px;
  border-radius: 2px;
}

.problem-description p {
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  font-family: 'Noto Serif SC', serif;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

@media (min-width: 768px) {
  .data-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .data-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.data-item {
  background: white;
  padding: 10px 12px;
  border-radius: 6px;
  border-left: 3px solid #0085C3;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.data-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.data-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 2px;
  font-weight: 500;
}

.data-value {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 2px;
  line-height: 1.2;
}

.data-context {
  font-size: 10px;
  color: #9ca3af;
  line-height: 1.3;
}

.solution-list {
  display: grid;
  gap: 8px;
}

@media (min-width: 768px) {
  .solution-list {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

.solution-item {
  background: white;
  padding: 12px 14px;
  border-radius: 6px;
  border-left: 3px solid #10b981;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.solution-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.solution-type {
  font-size: 12px;
  font-weight: 600;
  color: #059669;
  margin-bottom: 4px;
  font-family: 'Noto Serif SC', serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.solution-desc {
  font-size: 13px;
  color: #374151;
  margin-bottom: 4px;
  line-height: 1.4;
  font-family: 'Noto Serif SC', serif;
}

.solution-source {
  font-size: 10px;
  color: #6b7280;
  font-style: italic;
  font-family: 'Noto Serif SC', serif;
}

/* 滚动条样式 */
.issue-details::-webkit-scrollbar {
  width: 6px;
}

.issue-details::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.issue-details::-webkit-scrollbar-thumb {
  background: #0085C3;
  border-radius: 3px;
}

/* 数据和解决方案部分的整体优化 */
.data-support, .solutions {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

/* 小屏幕优化 */
@media (max-width: 767px) {
  .data-grid {
    grid-template-columns: 1fr;
  }
  
  .solution-list {
    grid-template-columns: 1fr;
  }
  
  .data-item, .solution-item {
    padding: 8px 10px;
  }
  
  .data-value {
    font-size: 14px;
  }
  
  .solution-desc {
    font-size: 12px;
  }
}
</style>